// ============================================================
// 📄 GERADOR DO LME (B211) — Laudo de Solicitação, Avaliação e
//    Autorização de Medicamento(s) do Componente Especializado da
//    Assistência Farmacêutica (CEAF / "alto custo")
// ============================================================
// Reproduz, em HTML imprimível FIEL ao formulário oficial do Ministério da
// Saúde (campos 1–23, cantos arredondados), o LME exigido para dispensação de
// medicamentos do Componente Especializado — no nosso uso: imunossupressores
// do transplante (tacrolimo, micofenolato, sirolimo, everolimo, azatioprina,
// ciclosporina) e ácido ursodesoxicólico.
//
// Diferente dos formulários do Pedido de Exames: o LME é disparado pelo
// RECEITUÁRIO (por MEDICAMENTO). Os medicamentos do CEAF presentes na receita
// entram na tabela 7/8 (quantidade por mês, 1º–6º). O médico preenche, pelo
// modal, os dados que não vêm do prontuário (peso, altura, raça/cor, CID,
// diagnóstico, anamnese, etc.). Sai junto com a receita, no MESMO documento.
//
// 100% client-side, sem biblioteca de PDF e sem logar conteúdo clínico (LGPD).

import { Paciente } from "../../../types";
import { abrirPDFEmNovaJanela } from "./gerarPDF";
import { escapeHtml, formatarDataBR } from "./gerarFichaGAL";
import { SolicitanteCargaViral } from "./gerarFichaCargaViral";
import susLogoBase64 from "./susLogoBase64";

// Reusa o solicitante (mesmos campos do cadastro: profissional + unidade).
// O LME usa o CNS do profissional (campo 15), como a GAL.
export type SolicitanteLME = SolicitanteCargaViral;

// Um medicamento do CEAF na tabela 7/8: nome (apresentação inclusa) +
// quantidade solicitada por mês (6 posições, 1º–6º mês).
export interface MedicamentoLME {
  nome: string; // ex.: "Tacrolimo 1mg, Cápsula"
  quantidades: string[]; // 6 entradas (mês 1..6) — string p/ aceitar em branco
}

// Dados preenchidos no modal (persistidos junto à receita).
export interface DadosLME {
  // --- Paciente (campos 4/5/6/19/20/21/22) — semeados do prontuário, editáveis ---
  nomeMae: string; // campo 4
  pesoPaciente: string; // campo 5 (kg)
  alturaPaciente: string; // campo 6 (cm)
  racaCor: string; // campo 19 — "Branca"|"Preta"|"Parda"|"Amarela"|"Indígena"
  etnia: string; // campo 19 — só se Indígena
  telefonePaciente: string; // campo 20
  emailPaciente: string; // campo 22
  docTipo: "CPF" | "CNS"; // campo 21 — qual documento do paciente
  // --- Justificativa (campos 9/10/11/12/13/16/18) ---
  cid10: string; // campo 9
  diagnostico: string; // campo 10
  anamnese: string; // campo 11
  tratamentoPrevio: "sim" | "nao"; // campo 12
  tratamentoPrevioRelato: string; // campo 12 (se sim)
  incapaz: "sim" | "nao"; // campo 13 — atestado de capacidade
  nomeResponsavel: string; // campo 13 (se incapaz)
  preenchidoPor: string; // campo 18 — quem preenche os campos abaixo
  dataSolicitacao: string; // campo 16 (dd/mm/aaaa)
  // --- Medicamentos do CEAF (campos 7/8) ---
  medicamentos: MedicamentoLME[];
}

// Opções de raça/cor do formulário oficial (campo 19).
export const OPCOES_RACA_COR = [
  "Branca",
  "Preta",
  "Parda",
  "Amarela",
  "Indígena",
] as const;

// Opções do campo 18 (quem preenche os campos do paciente).
export const OPCOES_PREENCHIDO_POR = [
  "Paciente",
  "Mãe do paciente",
  "Responsável (descrito no item 13)",
  "Médico solicitante",
] as const;

// ------------------------------------------------------------
// HELPERS DE CÉLULA (grade de 12 colunas, caixas de cantos arredondados)
// ------------------------------------------------------------
interface OpcoesCelula {
  span?: number;
  obrig?: boolean;
  alto?: boolean; // caixa de texto mais alta (anamnese, capacidade)
  estilo?: string; // grid-column/row explícito
}

// Caixa com rótulo "N- LABEL" sobreposto à borda superior (estilo legenda),
// valor preenchido abaixo. Cantos arredondados, fiel ao formulário oficial.
function campo(
  num: string,
  rotulo: string,
  valor: string,
  opts: OpcoesCelula = {}
): string {
  const { span = 1, obrig = false, alto = false, estilo = "" } = opts;
  const gc = estilo || `grid-column: span ${span};`;
  return `
    <div class="lme-campo${alto ? " lme-alto" : ""}" style="${gc}">
      <div class="lme-num">${num ? `${num}- ` : ""}${rotulo}${
    obrig ? '<span class="lme-ob">*</span>' : ""
  }</div>
      <div class="lme-fill">${valor || "&nbsp;"}</div>
    </div>`;
}

const linha = (campos: string, estilo = ""): string =>
  `<div class="lme-linha" style="${estilo}">${campos}</div>`;

// ------------------------------------------------------------
// TABELA DE MEDICAMENTOS (campos 7 e 8)
// ------------------------------------------------------------
function tabelaMedicamentos(meds: MedicamentoLME[]): string {
  // O formulário oficial tem 6 linhas fixas; preenchemos as dos medicamentos
  // do CEAF e deixamos o restante em branco (mínimo 6 linhas).
  const totalLinhas = Math.max(6, meds.length);
  let linhasHtml = "";
  for (let i = 0; i < totalLinhas; i++) {
    const m = meds[i];
    const qs = m?.quantidades || [];
    const cols = Array.from({ length: 6 }, (_, j) => {
      return `<td class="lme-q">${escapeHtml(qs[j] || "")}&nbsp;</td>`;
    }).join("");
    linhasHtml += `
      <tr>
        <td class="lme-mednum">${i + 1}</td>
        <td class="lme-medname">${escapeHtml(m?.nome || "")}&nbsp;</td>
        ${cols}
      </tr>`;
  }
  return `
    <table class="lme-tab">
      <thead>
        <tr>
          <th class="lme-mednum">&nbsp;</th>
          <th class="lme-medname">7- Medicamento(s)<span class="lme-ob">*</span></th>
          <th colspan="6" class="lme-qtdcab">8- Quantidade solicitada<span class="lme-ob">*</span></th>
        </tr>
        <tr>
          <th class="lme-mednum">&nbsp;</th>
          <th class="lme-medname">&nbsp;</th>
          <th class="lme-q">1º mês</th>
          <th class="lme-q">2º mês</th>
          <th class="lme-q">3º mês</th>
          <th class="lme-q">4º mês</th>
          <th class="lme-q">5º mês</th>
          <th class="lme-q">6º mês</th>
        </tr>
      </thead>
      <tbody>${linhasHtml}</tbody>
    </table>`;
}

// ------------------------------------------------------------
// MIOLO DO LAUDO (sem <html>/<style>/@page)
// ------------------------------------------------------------
function corpoLME(
  paciente: Paciente,
  solicitante: SolicitanteLME,
  dados: DadosLME,
  // B212 F4 (AI_126) — selo ICP-Brasil (compacto) nos campos 17 e 23, só no
  // PDF assinado. Sem selo = comportamento normal (impressão p/ assinar à mão).
  selo?: string
): string {
  const cnsPaciente = paciente.cns || paciente.hospub || "";
  const docPaciente =
    dados.docTipo === "CNS" ? cnsPaciente : paciente.cpf || "";

  return `
  <div class="lme">
    <!-- CABEÇALHO -->
    <div class="lme-cab">
      <img class="lme-logo" src="${susLogoBase64}" alt="Logo do SUS" />
      <div class="lme-cab-txt">
        <div><b>Sistema Único de Saúde</b></div>
        <div>Ministério da Saúde</div>
        <div>Secretaria de Estado da Saúde</div>
      </div>
    </div>

    <div class="lme-barra-preta">COMPONENTE ESPECIALIZADO DA ASSISTÊNCIA FARMACÊUTICA</div>
    <div class="lme-barra-cinza">LAUDO DE SOLICITAÇÃO, AVALIAÇÃO E AUTORIZAÇÃO DE MEDICAMENTO(S)</div>
    <div class="lme-subtitulo">SOLICITAÇÃO DE MEDICAMENTO(S)</div>
    <div class="lme-secao">CAMPOS DE PREENCHIMENTO EXCLUSIVO PELO MÉDICO SOLICITANTE</div>

    <div class="lme-grade">
      ${linha(
        campo("1", "Número do CNES", escapeHtml(solicitante.cnes || ""), { span: 4, obrig: true }) +
          campo("2", "Nome do estabelecimento de saúde solicitante", escapeHtml(solicitante.unidadeSaude || ""), { span: 8 })
      )}
      ${linha(
        campo("3", "Nome completo do Paciente", escapeHtml(paciente.nome), { span: 9, obrig: true }) +
          campo("5", "Peso do paciente (kg)", escapeHtml(dados.pesoPaciente), { span: 3, obrig: true })
      )}
      ${linha(
        campo("4", "Nome da Mãe do Paciente", escapeHtml(dados.nomeMae), { span: 9, obrig: true }) +
          campo("6", "Altura do paciente (cm)", escapeHtml(dados.alturaPaciente), { span: 3, obrig: true })
      )}
    </div>

    ${tabelaMedicamentos(dados.medicamentos)}

    <div class="lme-grade">
      ${linha(
        campo("9", "CID-10", escapeHtml(dados.cid10), { span: 3, obrig: true }) +
          campo("10", "Diagnóstico", escapeHtml(dados.diagnostico), { span: 9 })
      )}
      ${linha(
        campo("11", "Anamnese", escapeHtml(dados.anamnese), { span: 12, obrig: true, alto: true })
      )}
      ${linha(
        campo(
          "12",
          "Paciente realizou tratamento prévio ou está em tratamento da doença?",
          // B289 (AI_127) — só a opção marcada no modal (não todas).
          dados.tratamentoPrevio === "sim"
            ? `SIM. Relatar: ${escapeHtml(dados.tratamentoPrevioRelato || "")}`
            : dados.tratamentoPrevio === "nao"
            ? "NÃO"
            : "",
          { span: 12, obrig: true }
        )
      )}
      ${linha(
        campo(
          "13",
          "Atestado de capacidade",
          `<div class="lme-mini">A solicitação do medicamento deverá ser realizada pelo paciente. Entretanto, fica dispensada a obrigatoriedade da presença física do paciente considerado incapaz de acordo com os artigos 3º e 4º do Código Civil. O paciente é considerado incapaz?</div>` +
            // B289 (AI_127) — só a opção marcada no modal (não todas).
            (dados.incapaz === "sim"
              ? `SIM. Nome do responsável: ${escapeHtml(dados.nomeResponsavel || "")}`
              : dados.incapaz === "nao"
              ? "NÃO"
              : ""),
          { span: 12, obrig: true, alto: true }
        )
      )}
    </div>

    <!-- BLOCO DO SOLICITANTE: 14/15/16 à esquerda, 17 à direita (2 linhas) -->
    <div class="lme-grade lme-grade-assin">
      ${campo("14", "Nome do médico solicitante", escapeHtml(solicitante.nomeProfissional), {
        obrig: true,
        estilo: "grid-column: 1 / span 8; grid-row: 1;",
      })}
      ${campo(
        "17",
        "Assinatura e carimbo do médico",
        selo ||
          `<div class="lme-assin">${escapeHtml(solicitante.nomeProfissional)}<br/>${escapeHtml(
            solicitante.registroConselho
          )}</div>`,
        { obrig: true, alto: true, estilo: "grid-column: 9 / span 4; grid-row: 1 / span 2;" }
      )}
      ${campo("15", "Nº do Cartão Nacional de Saúde (CNS) do médico solicitante", escapeHtml(solicitante.cnsProfissional || ""), {
        obrig: true,
        estilo: "grid-column: 1 / span 5; grid-row: 2;",
      })}
      ${campo("16", "Data da solicitação", escapeHtml(dados.dataSolicitacao), {
        obrig: true,
        estilo: "grid-column: 6 / span 3; grid-row: 2;",
      })}
    </div>

    <!-- CAMPO 18 -->
    <div class="lme-secao lme-secao-fina">18- CAMPOS ABAIXO PREENCHIDOS POR<span class="lme-ob">*</span>:
      &nbsp; ${escapeHtml(dados.preenchidoPor || "")}
    </div>

    <div class="lme-grade">
      ${linha(
        campo(
          "19",
          "Raça/Cor/Etnia informado pelo paciente ou responsável",
          // B289 (AI_127) — só a opção marcada no modal (não todas).
          escapeHtml(dados.racaCor || "") +
            (dados.racaCor === "Indígena" && dados.etnia
              ? ` — Etnia: ${escapeHtml(dados.etnia)}`
              : ""),
          { span: 8, obrig: true }
        ) +
          campo("20", "Telefone(s) para contato do paciente", escapeHtml(dados.telefonePaciente), { span: 4 })
      )}
      ${linha(
        campo(
          "21",
          "Número do documento do paciente",
          // B289 (AI_127) — só o tipo de documento marcado no modal (não ambos).
          `${dados.docTipo === "CNS" ? "CNS" : "CPF"}: ${escapeHtml(docPaciente)}`,
          { span: 8 }
        ) +
          campo(
            "23",
            "Assinatura do responsável pelo preenchimento",
            // B212 F4 (AI_126) — o campo 23 é a assinatura de QUEM preencheu (campo
            // 18). Só recebe o selo digital do médico quando ELE preencheu
            // ("Médico solicitante"); se foi o paciente/responsável, fica em branco
            // para essa pessoa assinar à mão.
            selo && dados.preenchidoPor === "Médico solicitante" ? selo : "&nbsp;",
            { span: 4, obrig: true }
          )
      )}
      ${linha(campo("22", "Correio eletrônico do paciente", escapeHtml(dados.emailPaciente), { span: 12 }))}
    </div>

    <div class="lme-rodape">
      <span>* CAMPOS DE PREENCHIMENTO OBRIGATÓRIO</span>
      <span>Para suporte: ceaf.daf@saude.gov.br</span>
    </div>
  </div>`;
}

// ------------------------------------------------------------
// ESTILOS
// ------------------------------------------------------------
const ESTILOS_LME = `
  .lme { font-family: Arial, Helvetica, sans-serif; color: #000; font-size: 8.4px; line-height: 1.2; border: 1.5px solid #000; padding: 6px 8px 8px; }
  .lme-cab { display: flex; align-items: center; gap: 10px; padding: 2px 2px 6px; }
  .lme-logo { width: 46px; height: auto; display: block; }
  .lme-cab-txt { font-size: 9px; line-height: 1.3; }
  .lme-cab-txt b { font-size: 10px; }
  .lme-barra-preta { background: #000; color: #fff; text-align: center; font-weight: 700; font-size: 10px; letter-spacing: .3px; padding: 3px 4px; }
  .lme-barra-cinza { background: linear-gradient(#e9e9e9, #c4c4c4); color: #000; text-align: center; font-weight: 700; font-size: 11px; letter-spacing: .3px; padding: 3px 4px; border: 1px solid #888; border-top: none; }
  .lme-subtitulo { text-align: center; font-weight: 700; font-size: 10px; padding: 3px 4px; }
  .lme-secao { border: 1.2px solid #000; text-align: center; font-weight: 700; font-size: 8.6px; padding: 3px 4px; margin-bottom: 6px; }
  .lme-secao-fina { text-align: left; margin: 6px 0; }
  .lme-grade { display: grid; grid-template-columns: repeat(12, 1fr); gap: 7px 7px; margin-bottom: 6px; }
  .lme-grade-assin { grid-auto-rows: minmax(28px, auto); }
  .lme-linha { display: grid; grid-template-columns: repeat(12, 1fr); gap: 7px; grid-column: 1 / -1; }
  .lme-campo { position: relative; border: 1px solid #6b7785; border-radius: 9px; padding: 9px 7px 4px; min-height: 30px; background: #fff; }
  .lme-alto { min-height: 54px; }
  .lme-num { position: absolute; top: -6px; left: 8px; background: #fff; padding: 0 4px; font-size: 7.2px; font-weight: 700; color: #1a1a1a; white-space: nowrap; max-width: calc(100% - 12px); overflow: hidden; text-overflow: ellipsis; }
  .lme-ob { color: #b00; margin-left: 1px; }
  .lme-fill { margin-top: 2px; font-size: 10.5px; font-weight: 600; white-space: pre-wrap; word-break: break-word; }
  .lme-mini { font-weight: 400; font-size: 7.6px; line-height: 1.25; margin: 1px 0 3px; }
  .lme-ck { font-size: 11px; margin-right: 2px; vertical-align: -1px; }
  .lme-assin { font-weight: 700; font-size: 8.6px; line-height: 1.3; }
  /* Tabela de medicamentos */
  .lme-tab { width: 100%; border-collapse: collapse; margin-bottom: 8px; border: 1px solid #6b7785; border-radius: 9px; overflow: hidden; }
  .lme-tab th, .lme-tab td { border: 1px solid #9aa3ad; padding: 3px 4px; font-size: 8.2px; }
  .lme-tab thead th { background: #eef1f4; font-weight: 700; text-align: center; }
  .lme-mednum { width: 16px; text-align: center; font-weight: 700; }
  .lme-medname { text-align: left; font-weight: 600; }
  .lme-qtdcab { text-align: center; }
  .lme-q { width: 50px; text-align: center; }
  .lme-rodape { display: flex; justify-content: space-between; font-size: 7.4px; color: #222; margin-top: 4px; font-weight: 700; }
`;

// ------------------------------------------------------------
// API PÚBLICA
// ------------------------------------------------------------
/** Fragmento p/ embutir no MESMO documento da receita (folha extra). */
export function montarFragmentoLME(
  paciente: Paciente,
  solicitante: SolicitanteLME,
  dados: DadosLME,
  // B212 F4 (AI_126) — selo ICP-Brasil (compacto) nos campos 17 e 23, só no PDF assinado.
  selo?: string
): { estilos: string; corpo: string } {
  const estilos = `
    @page lme { size: A4 portrait; margin: 6mm; }
    .folha-lme { page: lme; page-break-before: always; }
    ${ESTILOS_LME}
  `;
  const corpo = `<div class="folha-lme">${corpoLME(paciente, solicitante, dados, selo)}</div>`;
  return { estilos, corpo };
}

/** Documento AUTÔNOMO do LME (sem a receita comum). */
function montarHTML(
  paciente: Paciente,
  solicitante: SolicitanteLME,
  dados: DadosLME
): string {
  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8" />
  <title>LME — ${escapeHtml(paciente.nome)}</title>
  <style>
    @page { size: A4 portrait; margin: 6mm; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: Arial, Helvetica, sans-serif; color: #000; margin: 0; background: #e9e9e9; }
    .barra-botoes { text-align: center; padding: 14px; }
    .lme { width: 198mm; margin: 0 auto 20px; background: #fff; }
    ${ESTILOS_LME}
    @media print {
      body { background: #fff; }
      .lme { width: auto; margin: 0; }
      .barra-botoes { display: none; }
    }
  </style>
</head>
<body>
  <div class="barra-botoes">
    <button onclick="window.print()" style="padding: 10px 32px; background: #2563eb; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; margin-right: 8px;">
      🖨️ Imprimir / Salvar PDF
    </button>
    <button onclick="window.close()" style="padding: 10px 32px; background: #6b7280; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer;">
      ✕ Fechar
    </button>
  </div>
  ${corpoLME(paciente, solicitante, dados)}
</body>
</html>`;
}

export function gerarFichaLME(
  paciente: Paciente,
  solicitante: SolicitanteLME,
  dados: DadosLME
): void {
  abrirPDFEmNovaJanela(montarHTML(paciente, solicitante, dados));
}
